import { useEffect } from "react";

import {
  Grid,
  Button,
  IconButton,
  Typography,
  ThemeProvider,
  useTheme
} from "@mui/material";
import { Add as AddIcon, Cancel as CancelIcon } from "@mui/icons-material";
import Warning from "@mui/icons-material/Warning";

import { DefaultPaper, TextField } from "@aclymatepackages/atoms";
import mainTheme from "@aclymatepackages/themes";
import { formatDecimal } from "@aclymatepackages/formatters";

import MaterialsInterface from "./MaterialsInterface";
import ProductMaterials from "./ProductMaterialsAutocomplete";

import { calculateMaterialsInputWeightsSum } from "../../../helpers/components/products";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const ComponentsInterface = ({
  components,
  setComponents,
  productWeightUnit,
  totalProductWeight,
  isElectronic,
  disabled,
  footprintShared,
  componentFootprints = []
}) => {
  const { palette } = useTheme();
  const [{ isSubscribed }] = useProductsEmissionsFactors();

  useEffect(() => {
    if (!isElectronic) {
      return;
    }
    const hasElectronicsComponent = components.some(
      ({ name }) => name === "Electronics"
    );
    if (hasElectronicsComponent) {
      return;
    }
    setComponents((current) => [
      ...current,
      {
        localId: crypto.randomUUID(),
        firestoreId: null,
        name: "Electronics",
        materials: [
          {
            type: "li-ion",
            label: "Li-ion Battery",
            weightUnit: productWeightUnit,
            isElectronics: true
          },
          {
            type: "electronics",
            label: "Electronics",
            weightUnit: productWeightUnit,
            isElectronics: true
          }
        ],
        productWeight: 0
      }
    ]);
  }, [isElectronic]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateComponent = (localId, updates) =>
    setComponents(
      components.map((c) => (c.localId === localId ? { ...c, ...updates } : c))
    );

  const updateComponentMaterials = (localId, materials) => {
    const productWeight = calculateMaterialsInputWeightsSum(
      materials,
      totalProductWeight
    );
    updateComponent(localId, { materials, productWeight });
  };

  const removeComponent = (localId) =>
    setComponents(components.filter((c) => c.localId !== localId));

  const addComponent = () =>
    setComponents([
      ...components,
      {
        localId: crypto.randomUUID(),
        firestoreId: null,
        name: "",
        materials: [
          { type: null, weight: null, weightUnit: productWeightUnit }
        ],
        productWeight: 0
      }
    ]);

  const totalComponentsWeight = components.reduce(
    (sum, { productWeight = 0 }) => sum + Number(productWeight),
    0
  );

  const hasWeightMismatch =
    !!totalProductWeight &&
    Math.round(totalComponentsWeight) !== Math.round(totalProductWeight);

  const buildMaterialsWithFootprints = (materials, compFootprint) => {
    const { materialsWithFootprints = [] } = compFootprint || {};
    return materials.map((material, index) => ({
      ...material,
      footprintKgs: materialsWithFootprints[index]?.footprintKgs
    }));
  };

  return (
    <Grid container spacing={2} direction="column">
      {components.map((component, index) => {
        const compFootprint = componentFootprints[index];
        const materialsWithFootprints = buildMaterialsWithFootprints(
          component.materials,
          compFootprint
        );

        return (
          <Grid item key={component.localId}>
            <DefaultPaper
              style={{ backgroundColor: palette.backgroundGray.main }}
            >
              <ThemeProvider theme={mainTheme}>
                <Grid container spacing={2} direction="column">
                  <Grid
                    item
                    container
                    alignItems="center"
                    spacing={1}
                    wrap="nowrap"
                  >
                    <Grid item xs={5}>
                      <TextField
                        label="Component Name"
                        value={component.name || ""}
                        setValue={(name) =>
                          updateComponent(component.localId, { name })
                        }
                        fullWidth
                        size="small"
                        disabled={disabled}
                      />
                    </Grid>
                    {compFootprint?.footprintKgs !== undefined && (
                      <Grid item xs>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          align="right"
                        >
                          <span
                            style={{
                              filter:
                                isSubscribed || footprintShared
                                  ? "none"
                                  : "blur(4px)"
                            }}
                          >
                            {formatDecimal(compFootprint.footprintKgs)}
                          </span>{" "}
                          kgs CO2e
                        </Typography>
                      </Grid>
                    )}
                    {components.length > 1 && (
                      <Grid item>
                        <IconButton
                          onClick={() => removeComponent(component.localId)}
                          size="small"
                          disabled={disabled}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                  <Grid item>
                    <MaterialsInterface
                      type="materials"
                      items={materialsWithFootprints}
                      setItems={(materials) =>
                        updateComponentMaterials(component.localId, materials)
                      }
                      MaterialComponent={ProductMaterials}
                      productWeightUnit={productWeightUnit}
                      totalWeight={component.productWeight}
                      disabled={disabled}
                      footprintShared={footprintShared}
                    />
                  </Grid>
                </Grid>
              </ThemeProvider>
            </DefaultPaper>
          </Grid>
        );
      })}
      {!disabled && (
        <Grid item container justifyContent="center">
          <Grid item>
            <ThemeProvider theme={mainTheme}>
              <Button
                onClick={addComponent}
                startIcon={<AddIcon />}
                size="small"
                color="secondary"
              >
                Add Component
              </Button>
            </ThemeProvider>
          </Grid>
        </Grid>
      )}
      {hasWeightMismatch && (
        <Grid item>
          <DefaultPaper style={{ backgroundColor: palette.error.main }}>
            <Grid container spacing={2} alignItems="center" flexWrap="nowrap">
              <Grid item>
                <Warning style={{ color: "white", fontSize: 50 }} />
              </Grid>
              <Grid item>
                <Typography variant="subtitle1">
                  Your product weight is{" "}
                  <strong>
                    {totalProductWeight} {productWeightUnit}
                  </strong>{" "}
                  but the total weight of your components is{" "}
                  <strong>
                    {formatDecimal(totalComponentsWeight)} {productWeightUnit}
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </DefaultPaper>
        </Grid>
      )}
    </Grid>
  );
};
export default ComponentsInterface;
